/**
 * Google Analytics 4 Integration (Consent-Gated)
 *
 * Privacy-focused analytics to track:
 * - Page views
 * - Sign-ups and conversions
 * - Key feature usage (trade entries, portfolio views)
 * - Premium interest
 *
 * Privacy Features:
 * - IP anonymization enabled
 * - No personal data tracked
 * - Cookie consent respected - all tracking is gated by consent
 * - GDPR compliant
 *
 * The shared Google tag is initialized by the existing consent vendor layer.
 * All tracking functions check consent before executing.
 */

import { hasConsent } from './consent/storage'
import { isWebMeasurementAllowed } from './measurementPolicy'
import { safeAnalyticsParameters } from './analyticsPrivacy'
import { setDataFastUser } from './consent/datafast'
export { sendDataFastPageView as trackDataFastPageView } from './consent/datafast'
import {
  initializeGoogleTag,
  sendGoogleAnalyticsEvent,
  sendGoogleAnalyticsPageView,
} from './consent/googleTag'

/**
 * Initialize Google Analytics 4
 * This is now called from vendors.ts with dynamic import
 * @deprecated Use initAnalyticsVendors() from consent/vendors.ts instead
 */
export const initGA = async () => {
  if (!import.meta.env.VITE_GA_MEASUREMENT_ID) {
    console.warn('Google Analytics: VITE_GA_MEASUREMENT_ID not configured')
    return
  }
  initializeGoogleTag()
}

/**
 * Track page views (for SPA navigation)
 * Only tracks if GA has been initialized (consent granted)
 */
export const trackPageView = (path: string, title?: string) => {
  try {
    sendGoogleAnalyticsPageView(path, title || document.title)
  } catch (error) {
    console.error('Google Analytics: Failed to track page view', error)
  }
}

/**
 * Track custom events
 * Only tracks if GA has been initialized (consent granted)
 */
export const trackEvent = (category: string, action: string, label?: string, value?: number) => {
  try {
    sendGoogleAnalyticsEvent(action, {
      event_category: category,
      event_label: label,
      value,
    })
  } catch (error) {
    console.error('Google Analytics: Failed to track event', error)
  }
}

/**
 * Pre-defined event tracking helpers for common actions
 */

// Authentication Events
export const trackSignUp = (method: 'email' | 'google' | 'apple') => {
  // Confirmed signup outcomes come from the existing backend funnel relay.
  void method
}

export const trackSignIn = (method: 'email' | 'google' | 'apple') => {
  trackEvent('User', 'Sign In', method)
}

export const trackSignOut = () => {
  trackEvent('User', 'Sign Out')
}

// Portfolio Events
export const trackAddPosition = (strategy: string) => {
  trackEvent('Portfolio', 'Add Position', strategy)
}

export const trackClosePosition = (strategy: string) => {
  trackEvent('Portfolio', 'Close Position', strategy)
}

export const trackViewPortfolio = () => {
  trackEvent('Portfolio', 'View Portfolio')
}

export const trackPortfolioTabView = (tab: string) => {
  trackEvent('Portfolio', 'Tab View', tab)
  trackDataFastGoal('portfolio_tab_viewed', { tab })
}

export const trackPortfolioTabTimeSpent = (tab: string, seconds: number) => {
  trackEvent('Portfolio', 'Tab Time Spent', tab, seconds)

  trackDataFastGoal('portfolio_tab_duration', {
    tab,
    seconds: seconds.toString(),
    duration_bucket: getDurationBucket(seconds),
  })
}

// Premium Interest Events
export const trackPremiumInterest = (source: string) => {
  trackEvent('Premium', 'Show Interest', source)
}

export const trackUpgradeModalOpen = () => {
  trackEvent('Premium', 'Open Upgrade Modal')
}

// Contact & Support Events
export const trackContactFormSubmit = () => {
  trackEvent('Support', 'Submit Contact Form')
}

export const trackEarlyTesterInterest = () => {
  trackEvent('Premium', 'Early Tester Interest')
}

// Feature Usage Events
export const trackCSVImport = () => {
  trackEvent('Feature', 'CSV Import')
}

export const trackAlertConfigured = (alertType: string) => {
  trackEvent('Feature', 'Configure Alert', alertType)
}

export type DiscoveryAnalyticsEvent =
  | 'discovery_screen_saved'
  | 'discovery_screen_loaded'
  | 'discovery_screen_updated'
  | 'discovery_screen_deleted'
  | 'discovery_filter_used'
  | 'discovery_result_opened'

/** Discovery analytics intentionally accept only non-sensitive UI metadata. */
export const trackDiscoveryEvent = (
  event: DiscoveryAnalyticsEvent,
  params?: { scan_id?: string; filter_name?: string }
) => {
  trackEvent('Discovery', event, params?.filter_name || params?.scan_id)
  trackDataFastGoal(event, params)
}

export type ScenarioLabAnalyticsEvent =
  | 'scenario_lab_opened'
  | 'scenario_created'
  | 'scenario_calculated'
  | 'scenario_saved'
  | 'scenario_reopened'
  | 'scenario_refreshed'
  | 'scenario_prepare_order_clicked'
  | 'scenario_order_instructions_copied'
  | 'scenario_ibkr_export_downloaded'

/** Scenario Lab analytics omit symbols, prices, quantities, portfolio IDs, and position IDs. */
export const trackScenarioLabEvent = (event: ScenarioLabAnalyticsEvent, entrySource: string) => {
  const allowedEntrySources = new Set([
    'direct',
    'portfolio',
    'broker_sync',
    'position',
    'screener',
    'discovery',
    'options_calculator',
    'roll_assistant',
    'duplicate',
  ])
  const params = { entry_source: allowedEntrySources.has(entrySource) ? entrySource : 'direct' }
  trackEvent('Scenario Lab', event, params.entry_source)
  trackDataFastGoal(event, params)
}

export type RollAssistantAnalyticsEvent =
  | 'roll_assistant_opened'
  | 'roll_candidates_generated'
  | 'roll_filters_changed'
  | 'roll_candidate_opened'
  | 'roll_candidate_scenario_created'
  | 'roll_candidate_saved'

interface RollAssistantAnalyticsParams {
  position_type?: 'short_put' | 'covered_or_short_call' | 'long_call' | 'long_put'
  pricing_mode?: 'mid' | 'natural' | 'custom'
  filter_name?: string
  candidate_count?: number
}

/** Roll Assistant analytics omit symbols, strikes, expirations, prices, portfolio IDs, and position IDs. */
export const trackRollAssistantEvent = (
  event: RollAssistantAnalyticsEvent,
  params?: RollAssistantAnalyticsParams
) => {
  const safeParams = {
    ...params,
    candidate_count:
      params?.candidate_count == null ? undefined : Math.max(0, params.candidate_count),
  }
  trackEvent(
    'Roll Assistant',
    event,
    safeParams.filter_name || safeParams.pricing_mode || safeParams.position_type
  )
  trackDataFastGoal(event, safeParams)
}

export type TradeCardAnalyticsEvent =
  | 'trade_card_created'
  | 'trade_card_downloaded'
  | 'trade_card_shared'

interface TradeCardAnalyticsParams {
  source: 'tracked' | 'closed' | 'scenario' | 'roll' | 'discovery'
  format: 'square' | 'landscape'
  includes_pnl: boolean
  includes_entry: boolean
  includes_current_price: boolean
}

/** Trade-card events intentionally omit symbols, prices, P/L values, trade IDs, and account data. */
export const trackTradeCardEvent = (
  event: TradeCardAnalyticsEvent,
  params: TradeCardAnalyticsParams
) => {
  trackEvent('Trade Card', event, `${params.source}:${params.format}`)
  trackDataFastGoal(event, { ...params })
}

export type PublicMarketingEvent =
  | 'feature_page_view'
  | 'pricing_cta'
  | 'comparison_cta'
  | 'scenario_page_cta'
  | 'roll_assistant_page_cta'
  | 'broker_sync_page_cta'

/** Public-site events contain only page and placement labels; consent gates both vendors. */
export const trackPublicMarketingEvent = (
  event: PublicMarketingEvent,
  params?: {
    page?: string
    placement?: string
    destination?: string
    plan?: string
  }
) => {
  const label = [params?.page, params?.placement, params?.destination, params?.plan]
    .filter(Boolean)
    .join(':')
  trackEvent('Public Marketing', event, label || undefined)
  trackDataFastGoal(event, params)
}

export type EdgeAnalyticsEvent =
  | 'edge_viewed'
  | 'edge_period_changed'
  | 'edge_strategy_opened'
  | 'edge_underlying_opened'

/** Your Edge events intentionally omit tickers, dollar values, and account identifiers. */
export const trackEdgeEvent = (
  event: EdgeAnalyticsEvent,
  params?: { period?: string; strategy_type?: string }
) => {
  trackEvent('Your Edge', event, params?.strategy_type || params?.period)
  trackDataFastGoal(event, params)
}

// Navigation Events
export const trackNavigateTo = (destination: string) => {
  trackEvent('Navigation', 'Navigate', destination)
}

// Landing Page Events
export const trackCTAClick = (ctaLocation: string, ctaText: string) => {
  trackEvent('Landing', 'CTA Click', `${ctaLocation}: ${ctaText}`)
}

export const trackScrollDepth = (depth: number) => {
  trackEvent('Landing', 'Scroll Depth', `${depth}%`, depth)
}

export const trackExitIntentShow = () => {
  trackEvent('Landing', 'Exit Intent Shown')
}

export const trackExitIntentDismiss = () => {
  trackEvent('Landing', 'Exit Intent Dismissed')
}

export const trackScreenshotView = (screenshotId: string) => {
  trackEvent('Landing', 'Screenshot View', screenshotId)
}

export const trackNewsletterSignup = (source: string) => {
  trackEvent('Newsletter', 'Subscribe', source)
}

// ============================================
// DataFast Goal Tracking
// ============================================

/**
 * Declare the DataFast global function type
 * Supports both goal tracking and user identification
 */
declare global {
  interface Window {
    datafast?: (action: string, params?: Record<string, string>) => void
  }
}

/**
 * Identify the current consented DataFast visitor by stable account UUID only.
 * Legacy profile options are accepted for caller compatibility and ignored.
 */
export interface DataFastIdentifyOptions {
  name?: string // Replaces anonymous name in visitor profile
  image?: string // Profile image URL (max 250 chars)
  [key: string]: string | undefined // Custom properties (max 10, values max 255 chars)
}

export const identifyDataFastUser = (
  userId: string | null,
  options?: DataFastIdentifyOptions
): void => {
  void options
  try {
    setDataFastUser(userId)
  } catch (error) {
    console.error('DataFast: Failed to update identity', error)
  }
}

/**
 * Track a custom goal in DataFast
 * Can be used for signups, CTA clicks, checkout initiation, etc.
 * @param goal - Goal name (lowercase, underscores/hyphens allowed, max 64 chars)
 * @param params - Optional custom parameters (max 10, values max 255 chars)
 */
export const trackDataFastGoal = (
  goal: string,
  params?: Record<string, string | number | boolean | null | undefined>
): boolean => {
  try {
    if (isWebMeasurementAllowed() && window.datafast && hasConsent('analytics')) {
      const sanitizedParams = sanitizeDataFastParams(params)
      if (sanitizedParams) {
        window.datafast(goal, sanitizedParams)
      } else {
        window.datafast(goal)
      }
      return true
    }
  } catch (error) {
    console.error('DataFast: Failed to track goal', error)
  }

  return false
}

const DATAFAST_PARAM_LIMIT = 10
const DATAFAST_PARAM_VALUE_LIMIT = 255

function sanitizeDataFastParams(
  params?: Record<string, string | number | boolean | null | undefined>
) {
  if (!params) return undefined

  const sanitized: Record<string, string> = {}

  Object.entries(safeAnalyticsParameters(params) || {})
    .filter(([, value]) => value !== undefined && value !== null)
    .slice(0, DATAFAST_PARAM_LIMIT)
    .forEach(([key, value]) => {
      const sanitizedKey = key
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, '_')
        .slice(0, 32)

      if (!sanitizedKey) return

      sanitized[sanitizedKey] = String(value).slice(0, DATAFAST_PARAM_VALUE_LIMIT)
    })

  return sanitized
}

export const getDurationBucket = (seconds: number): string => {
  if (seconds > 300) return '5m+'
  if (seconds > 120) return '2-5m'
  if (seconds > 60) return '1-2m'
  if (seconds > 30) return '31-60s'
  if (seconds > 10) return '11-30s'
  return '0-10s'
}

export type BrokerSyncDurationSurface =
  | 'landing_broker_integrations'
  | 'broker_sync_page'
  | 'ibkr_broker_sync_page'

const brokerSyncDurationGoals: Record<BrokerSyncDurationSurface, string> = {
  landing_broker_integrations: 'broker_integrations_section_duration',
  broker_sync_page: 'broker_sync_page_duration',
  ibkr_broker_sync_page: 'ibkr_broker_sync_page_duration',
}

export const trackBrokerSyncDuration = (
  surface: BrokerSyncDurationSurface,
  seconds: number,
  params?: Record<string, string>
) => {
  trackDataFastGoal(brokerSyncDurationGoals[surface], {
    surface,
    seconds: seconds.toString(),
    duration_bucket: getDurationBucket(seconds),
    ...params,
  })
}

// Pre-defined DataFast goal helpers
export const trackDataFastSignup = (method: 'email' | 'google' | 'apple') => {
  void method // Legacy completion is replaced by the canonical backend outcome.
}

export const trackDataFastSignupStarted = (method: 'email' | 'google' | 'apple') => {
  trackEvent('Auth', 'signup_started', method)
  trackDataFastGoal('signup_started', { method })
}

export const trackDataFastSignupPendingConfirmation = (method: 'email' | 'google' | 'apple') => {
  trackDataFastGoal('signup_pending_confirmation', { method })
}

export const trackDataFastSignupCompleted = (method: 'email' | 'google' | 'apple') => {
  void method // The backend owns completion, including email confirmation flows.
}

export type NativeAuthProvider = 'email' | 'google' | 'apple'
export type NativeWelcomeSectionId = 'hero' | 'portfolio' | 'wheel' | 'discovery'

export type NativeAuthErrorCategory =
  | 'cancelled'
  | 'duplicate_account'
  | 'validation'
  | 'network'
  | 'oauth'
  | 'callback'
  | 'unknown'

function trackNativeWelcomeGoal(
  goal: string,
  params?: Record<string, string | number | boolean | null | undefined>
) {
  trackEvent(
    'Native Welcome',
    goal,
    params ? Object.values(params).filter(Boolean).join(':').slice(0, 120) : undefined
  )
  trackDataFastGoal(goal, params)
}

export function normalizeNativeAuthErrorCategory(error: unknown): NativeAuthErrorCategory {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : String(error ?? '')
  const normalized = message.toLowerCase()

  if (normalized.includes('cancel') || normalized.includes('access_denied')) {
    return 'cancelled'
  }
  if (normalized.includes('already exists') || normalized.includes('duplicate')) {
    return 'duplicate_account'
  }
  if (
    normalized.includes('valid email') ||
    normalized.includes('password') ||
    normalized.includes('required') ||
    normalized.includes('invalid')
  ) {
    return 'validation'
  }
  if (
    normalized.includes('network') ||
    normalized.includes('fetch') ||
    normalized.includes('timeout') ||
    normalized.includes('offline')
  ) {
    return 'network'
  }
  if (
    normalized.includes('oauth') ||
    normalized.includes('provider') ||
    normalized.includes('google') ||
    normalized.includes('apple')
  ) {
    return 'oauth'
  }
  if (
    normalized.includes('callback') ||
    normalized.includes('code') ||
    normalized.includes('session')
  ) {
    return 'callback'
  }

  return 'unknown'
}

export function trackNativeWelcomeView(platform: string) {
  trackNativeWelcomeGoal('native_welcome_view', {
    platform,
  })
}

export function trackNativeSectionImpression(platform: string, section: NativeWelcomeSectionId) {
  trackNativeWelcomeGoal('native_welcome_section_impression', {
    platform,
    section,
  })
}

export function trackNativePrimaryCtaClicked(platform: string, provider: NativeAuthProvider) {
  trackNativeWelcomeGoal('native_primary_cta_clicked', {
    platform,
    provider,
    section: 'sticky_cta',
  })
}

export function trackNativeEmailAlternativeClicked(platform: string) {
  trackNativeWelcomeGoal('native_email_alternative_clicked', {
    platform,
    section: 'sticky_cta',
  })
}

export function trackNativeSigninClicked(platform: string) {
  trackNativeWelcomeGoal('native_signin_clicked', {
    platform,
    section: 'sticky_cta',
  })
}

export function trackNativeSignupStarted(
  platform: string,
  provider: NativeAuthProvider,
  entrySection: NativeWelcomeSectionId
) {
  trackNativeWelcomeGoal('native_signup_started', {
    platform,
    provider,
    entry_section: entrySection,
  })
}

export function trackNativeSigninStarted(platform: string, entrySection: NativeWelcomeSectionId) {
  trackNativeWelcomeGoal('native_signin_started', {
    platform,
    entry_section: entrySection,
  })
}

export function trackNativeSignupCompleted(
  platform: string,
  provider?: NativeAuthProvider,
  entrySection?: NativeWelcomeSectionId
) {
  trackNativeWelcomeGoal('native_signup_completed', {
    platform,
    provider,
    entry_section: entrySection,
  })
}

export function trackNativeAuthFailed(
  platform: string,
  provider: NativeAuthProvider,
  errorCategory: NativeAuthErrorCategory,
  entrySection?: NativeWelcomeSectionId
) {
  trackNativeWelcomeGoal('native_auth_failed', {
    platform,
    provider,
    error_category: errorCategory,
    entry_section: entrySection,
  })
}

export const trackDataFastCTAClick = (location: string, text: string) => {
  trackDataFastGoal('cta_click', { location, text: text.slice(0, 64) })
}

export interface LandingPageCTAParams {
  cta_id: string
  section: string
  cta_text: string
  destination: string
  plan?: string
  billing_interval?: string
  variant?: string
}

export const trackLandingPageCTAClick = (params: LandingPageCTAParams) => {
  trackDataFastGoal('lp_cta_clicked', { ...params })
}

export const trackDataFastCheckoutInitiated = (plan: 'monthly' | 'yearly') => {
  void plan // Replaced by the confirmed canonical checkout outcome.
}

export const trackDataFastCheckoutStarted = (
  plan: 'monthly' | 'yearly',
  params?: { billing_interval?: string; trial?: boolean }
) => {
  void plan
  void params // Replaced by the confirmed canonical checkout outcome.
}

export const trackDataFastPremiumUpgrade = (plan: 'monthly' | 'yearly') => {
  trackDataFastGoal('premium_upgrade', { plan })
}

export type ActivationEventName =
  | 'onboarding_started'
  | 'onboarding_completed'
  | 'portfolio_created'
  | 'first_position_added'
  | 'first_option_position_added'
  | 'strategy_created'
  | 'watchlist_created'

export const trackDataFastActivationEvent = (
  eventName: ActivationEventName,
  params?: Record<string, string | number | boolean | null | undefined>
) => {
  return trackDataFastGoal(eventName, params)
}
